"use client";
import Layout2 from "@/components/Layout/Layout2";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileDown, Search, Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import TableHistoryListTable from "@/components/table/table-history/table-history-list-table";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useSearch } from "@/hooks/useSearch";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import { useSelector, useDispatch } from "react-redux";
import { format, subYears, addYears } from "date-fns";
import useAuth from "@/hooks/useAuth";
import { fetchBookingHistory } from "@/redux/slice/table-slice/table-slice";
import Paginat from "@/components/lib/paginat";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import LeftBar2 from "@/components/Layout/LeftBar2";

function TableHistory() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");

  const {
    bookingHistory,
    bookingHistoryPagination,
    bookingHistoryLoading
  } = useSelector((state) => state.table.table);

  const defaultStart = subYears(new Date(), 1);
  const defaultEnd = addYears(new Date(), 1);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  // Handle Date Changes
  const handleStartChange = (date) => {
    setStartDate(date);
    setPage(1);
  };

  const handleEndChange = (date) => {
    setEndDate(date);
    setPage(1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearch(searchText);
      setPage(1);
    }, 900);

    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    if (!user?.restaurant_id) return;
    dispatch(
      fetchBookingHistory({
        restaurant_id: user.restaurant_id,
        page,
        per_page: perPage,
        start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
        end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
        search_term: search
      })
    );
  }, [user?.restaurant_id, page, perPage, startDate, endDate, search, dispatch]);

  return (
    <div className="mt-10 container px-4 sm:px-6 lg:px-8">
      {/* Search & Filters */}
      <div className="w-full flex justify-between xl:flex-row flex-col gap-5 items-start xl:items-center bg-cblack2 p-4 rounded-lg shadow-md">
        {/* Search Input */}
        <div className="w-full xl:w-[260px]">
          <div className="relative">
            <Search
              size={18}
              className="text-cwhite3 absolute top-2.5 left-3"
            />
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search"
              className="bg-cblack3 pl-10 h-10 border-gray-700 text-white"
            />
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-wrap gap-3 items-center w-full xl:w-auto justify-start xl:justify-end">
          <div className="flex flex-wrap gap-3 items-center text-white">
            {/* Start Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[140px] sm:w-[160px] justify-start text-left font-normal bg-cblack3 border border-gray-600 text-cwhite2 h-10"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "yyyy-MM-dd") : "Start Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-cblack3">
                <Calendar
                  className={"text-white"}
                  mode="single"
                  selected={startDate}
                  onSelect={handleStartChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <span className="hidden sm:inline">to</span>

            {/* End Date */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[140px] sm:w-[160px] justify-start text-left font-normal bg-cblack3 border border-gray-600 text-cwhite2 h-10"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "yyyy-MM-dd") : "End Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-cblack3">
                <Calendar
                  className={"text-white"}
                  mode="single"
                  selected={endDate}
                  onSelect={handleEndChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            {/* Export */}
            <Button className="bg-cblack3 border border-gray-600 text-cwhite2 h-10 px-4">
              <FileDown className="w-4 h-4 mr-2" /> Export
            </Button>

            {/* Per Page */}
            <div className="flex gap-2 justify-center items-center">
              <h3 className="text-white text-sm whitespace-nowrap">Show</h3>
              <Select
                onValueChange={(value) => {
                  setPerPage(Number(value));
                  setPage(1);
                }}
                value={perPage.toString()}
              >
                <SelectTrigger className="w-[70px] bg-cblack3 border-gray-600 h-10">
                  <SelectValue placeholder="10" />
                </SelectTrigger>
                <SelectContent className="bg-cblack3 text-white border-gray-700">
                  <SelectGroup>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="40">40</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-800 bg-cblack2">
        {bookingHistoryLoading ? (
          <div className="p-4">
            <OnlyTableSekeleton header={false} />
          </div>
        ) : (
          <div className="min-w-[1200px]">
            <TableHistoryListTable data={bookingHistory} />
          </div>
        )}
      </div>

      {bookingHistoryPagination && (
        <div className="mt-4 pb-4">
          <Paginat
            pagination={bookingHistoryPagination}
            onPageChange={(p) => setPage(p)}
          />
        </div>
      )}
    </div>
  );
}

export default TableHistory;
